import React from "react";
import { Card, CardBody, Col, Row } from "reactstrap";

const resolveProductNames = (productIds = [], menuItems = []) => {
  const productLookup = menuItems.reduce((acc, item) => {
    acc[String(item.productId)] = item.name;
    return acc;
  }, {});

  return productIds.map((itemConfig) => {
    if (itemConfig && typeof itemConfig === "object") {
      const productName = productLookup[String(itemConfig.productId)] || itemConfig.productId || "Unknown Product";
      const variantName = itemConfig.variantId ? (itemConfig.variantId === "any" ? "" : ` (${itemConfig.variantId})`) : "";
      return `${productName}${variantName}`;
    }
    return String(itemConfig);
  });
};

const resolveCategoryNames = (categoryIds = [], categories = []) => {
  const lookup = categories.reduce((acc, cat) => {
    const id = cat.id || cat.categoryId;
    acc[id] = cat.category || id;
    return acc;
  }, {});
  return categoryIds.map((id) => lookup[id] || id);
};

const renderQualification = (qualification, menuItems, categories) => {
  switch (qualification.type) {
    case "spendAmount":
      return `Spend ₹${qualification.minimumOrder || "0"}`;
    case "buyProduct":
      return `Buy ${qualification.quantity || "1"} × ${qualification.productIds.length ? resolveProductNames(qualification.productIds, menuItems).join(", ") : "product(s)"}`;
    case "buyCategory": {
      const catNames = resolveCategoryNames(qualification.categoryIds || [], categories);
      return `Buy ${qualification.quantity || "1"} from ${catNames.join(", ") || "category"}`;
    }
    case "buyBrand":
      return `Buy ${qualification.quantity || "1"} from brand: ${(qualification.brandIds || []).join(", ") || "brand"}`;
    case "buyCombo":
      return qualification.comboItems?.length
        ? `Buy combo: ${qualification.comboItems.map((item) => {
          const name = menuItems.find(m => m.productId === item.productId)?.name || item.productId;
          return `${item.quantity} × ${name}`;
        }).join(", ")}`
        : "Buy combo items";
    case "firstOrder":
      return "First order";
    case "freeDelivery":
      return "Free delivery qualification";
    case "custom":
      return qualification.customRule || "Custom qualification";
    default:
      return "Qualification rule";
  }
};

const renderReward = (reward, menuItems, categories) => {
  switch (reward.type) {
    case "flatDiscount":
      return `₹${reward.amount || "0"} off`;
    case "percentageDiscount":
      return `${reward.percent || "0"}% off`;
    case "freeProduct": {
      const qty = reward.quantity || "1";
      const target = (reward.targetType || "product").toLowerCase();
      if (target === "category") {
        const catNames = resolveCategoryNames(reward.rewardCategory ? [reward.rewardCategory] : [], categories);
        return `${qty} free from category: ${catNames[0] || reward.rewardCategory || "category"}`;
      }
      if (target === "brand") {
        return `${qty} free from brand: ${reward.rewardBrand || "brand"}`;
      }
      if (target === "combo") {
        const comboNames = (reward.rewardCombo || []).map(item => {
          return menuItems.find(m => m.productId === item.productId)?.name || item.productId || "item";
        });
        return `Free combo: ${comboNames.join(" + ") || "combo items"}`;
      }
      // Default: specific product
      return `${qty} ${(reward.productIds || []).length ? resolveProductNames(reward.productIds, menuItems).join(", ") : "free product(s)"}`;
    }
    case "freeDelivery":
      return "Free delivery";
    case "comboPrice":
      return `Combo price ₹${reward.amount || "0"}`;
    case "fixedPrice":
      return `Qualifying item(s) priced at ₹${reward.targetPrice || "0"}`;
    case "rewardPoints":
      return `${reward.points || "0"} points`;
    case "walletCredit":
      return `₹${reward.walletCredit || "0"} wallet credit`;
    default:
      return "Reward";
  }
};

const renderPolicy = (policy) => {
  if (!policy) return "No reward policy selected";
  return `${policy.selection.replace(/([A-Z])/g, " $1").trim()} - ${policy.rewardRepeatMode === "LIMITED" ? `${policy.rewardLimit} times` : "Unlimited"}`;
};

const renderRestrictions = (restrictions) => {
  const parts = [];
  if (restrictions.firstOrderOnly) parts.push("First order only");
  if (restrictions.deliveryTypes?.length) parts.push(`Delivery types: ${restrictions.deliveryTypes.join(", ")}`);
  if (restrictions.maximumUsage) parts.push(`Max usage ${restrictions.maximumUsage}`);
  if (restrictions.maximumUsagePerCustomer) parts.push(`Max per customer ${restrictions.maximumUsagePerCustomer}`);
  return parts.length ? parts.join(" · ") : "No restrictions";
};

const StepPreview = ({ promotion, menuItems = [], categories = [] }) => {
  return (
    <div>
      <h5 className="mb-3">Live Preview</h5>
      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col md={12}>
              <h6>{promotion.name || "Promotion preview"}</h6>
              <p className="text-muted mb-1">{promotion.description || "Summary of the promotion rules."}</p>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={promotion.reward.type === "freeProduct" ? 4 : 6}>
              <div className="border rounded p-3 h-100">
                <h6 className="mb-2">Qualification</h6>
                <p>{renderQualification(promotion.qualification, menuItems, categories)}</p>
              </div>
            </Col>
            <Col md={promotion.reward.type === "freeProduct" ? 4 : 6}>
              <div className="border rounded p-3 h-100">
                <h6 className="mb-2">Reward</h6>
                <p>{renderReward(promotion.reward, menuItems, categories)}</p>
              </div>
            </Col>
            {promotion.reward.type === "freeProduct" && (
              <Col md={4}>
                <div className="border rounded p-3 h-100">
                  <h6 className="mb-2">Policy</h6>
                  <p>{renderPolicy(promotion.policy)}</p>
                </div>
              </Col>
            )}
          </Row>

          <Row>
            <Col md={12}>
              <div className="border rounded p-3">
                <h6 className="mb-2">Restrictions</h6>
                <p>{renderRestrictions(promotion.restrictions)}</p>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  );
};

export default StepPreview;
